import { Fragment, useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import API, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { ArrowsClockwise, Copy, Warning, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Admin → System Diagnostics
 *
 * Built so the agent can debug prod issues by reading state directly.
 * All four tabs run against whatever DB the backend points at — load this
 * page in prod, hit "Copy diag bundle", paste the JSON in chat → I have
 * everything I need to diagnose.
 */
export default function AdminDiagnostics() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "runs" ? "runs"
    : searchParams.get("tab") === "postback" ? "postback"
    : "errors";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState({ rows: [], by_component: {}, by_exception_class: {} });
  const [runs, setRuns] = useState({ rows: [], by_component: {} });
  const [pbh, setPbh] = useState({ publishers: [] });
  const [loading, setLoading] = useState(true);
  const [windowH, setWindowH] = useState(24);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [h, e, r, p] = await Promise.all([
        API.get("/admin/diag/health"),
        API.get(`/admin/diag/errors?since_hours=${windowH}&limit=200`),
        API.get(`/admin/diag/sync-runs?since_hours=${windowH * 3}&limit=100`),
        API.get(`/admin/diag/postback-health?since_hours=${windowH}`),
      ]);
      setHealth(h.data);
      setErrors(e.data);
      setRuns(r.data);
      setPbh(p.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [windowH]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "runs" || tab === "postback" || tab === "errors") {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const failedSyncCount = health?.sync_runs_24h?.failed ?? 0;

  const copyBundle = async () => {
    try {
      const { data } = await API.get(`/admin/diag/export?since_hours=${windowH}`);
      const text = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${(text.length / 1024).toFixed(1)} KB diag bundle`);
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to export");
    }
  };

  const healthSeverity = (() => {
    if (!health) return "loading";
    if (health.errors_last_1h > 5 || health.sync_runs_24h.failed > 3 || health.sync_runs_24h.stuck_running > 0) return "critical";
    if (health.errors_last_24h > 10 || (health.postback_health_24h.success_rate_pct !== null && health.postback_health_24h.success_rate_pct < 90)) return "warn";
    return "ok";
  })();
  const sevPill = {
    ok: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "All systems OK", icon: <CheckCircle size={14} weight="fill" /> },
    warn: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Degraded", icon: <Warning size={14} weight="fill" /> },
    critical: { cls: "bg-red-50 text-red-700 border-red-200", label: "Active issues", icon: <XCircle size={14} weight="fill" /> },
    loading: { cls: "bg-slate-100 text-slate-500 border-slate-200", label: "…", icon: null },
  }[healthSeverity];

  return (
    <div className="p-8" data-testid="admin-diagnostics-page">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">System / Diagnostics</p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">Diagnostics</h1>
          <p className="text-sm text-slate-500 mt-2">
            Real-time view of errors, sync runs, and delivery health. Use "Copy diag bundle" to share state with support.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={windowH} onChange={(e) => setWindowH(Number(e.target.value))} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="diag-window-select">
            <option value={1}>Last 1h</option>
            <option value={6}>Last 6h</option>
            <option value={24}>Last 24h</option>
            <option value={72}>Last 72h</option>
            <option value={168}>Last 7d</option>
          </select>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="diag-refresh">
            <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={copyBundle} className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid="copy-diag-bundle">
            <Copy size={14} className="mr-1.5" /> Copy diag bundle
          </Button>
        </div>
      </div>

      {/* Health summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="System status"
          render={<span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${sevPill.cls}`}>{sevPill.icon}{sevPill.label}</span>}
          testId="diag-stat-status"
        />
        <StatCard
          label="Errors (1h / 24h)"
          render={<span className="font-mono">
            <span className={health && health.errors_last_1h > 0 ? "text-red-600" : "text-slate-900"}>{health?.errors_last_1h ?? "—"}</span>
            <span className="text-slate-400"> / {health?.errors_last_24h ?? "—"}</span>
          </span>}
          testId="diag-stat-errors"
        />
        <StatCard
          label="Sync runs (24h)"
          render={<span className="font-mono">
            <span className="text-emerald-600">{health?.sync_runs_24h?.ok ?? "—"}</span>
            <span className="text-slate-400"> / </span>
            <span className={health?.sync_runs_24h?.failed > 0 ? "text-red-600" : "text-slate-900"}>{health?.sync_runs_24h?.failed ?? "—"} failed</span>
            {health?.sync_runs_24h?.stuck_running > 0 && (
              <span className="ml-2 text-amber-600">{health.sync_runs_24h.stuck_running} stuck</span>
            )}
          </span>}
          testId="diag-stat-sync"
        />
        <StatCard
          label="Postback health (24h)"
          render={<span className="font-mono">
            <span className={(health?.postback_health_24h?.success_rate_pct ?? 100) < 90 ? "text-red-600" : "text-emerald-600"}>{health?.postback_health_24h?.success_rate_pct ?? "—"}%</span>
            <span className="text-slate-400"> · {health?.postback_health_24h?.failed ?? 0} failed of {health?.postback_health_24h?.total ?? 0}</span>
          </span>}
          testId="diag-stat-pb"
        />
      </div>

      {failedSyncCount > 0 && activeTab === "errors" && (errors.total ?? 0) === 0 && (
        <div className="mb-4 border border-amber-200 bg-amber-50 rounded-md px-4 py-3 text-sm text-amber-800" data-testid="diag-sync-hint">
          {failedSyncCount} sync run(s) failed in the last 24h but there are no error-log entries.
          {" "}
          <button type="button" className="font-semibold underline" onClick={() => setActiveTab("runs")}>
            View Sync runs tab →
          </button>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="border-b border-slate-200 bg-transparent h-auto p-0 mb-4 rounded-none">
          <TabsTrigger value="errors" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium" data-testid="tab-diag-errors">
            Errors ({errors.total ?? 0})
          </TabsTrigger>
          <TabsTrigger value="runs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium" data-testid="tab-diag-runs">
            Sync runs ({runs.total ?? 0})
          </TabsTrigger>
          <TabsTrigger value="postback" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-4 py-2.5 text-sm font-medium" data-testid="tab-diag-postback">
            Postback health
          </TabsTrigger>
        </TabsList>

        <TabsContent value="errors">
          <ErrorsTable errors={errors} />
        </TabsContent>

        <TabsContent value="runs">
          <SyncRunsTable runs={runs} />
        </TabsContent>

        <TabsContent value="postback">
          <PostbackHealthTable rows={pbh.publishers || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, render, testId }) {
  return (
    <div className="border border-slate-200 rounded-md p-4 bg-white" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
      <div className="text-lg font-bold text-slate-900">{render}</div>
    </div>
  );
}

function ErrorsTable({ errors }) {
  const rows = errors.rows || [];
  const byComp = errors.by_component || {};
  const byExc = errors.by_exception_class || {};
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SummaryCard title="By component" items={byComp} />
        <SummaryCard title="By exception class" items={byExc} />
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-32">Time</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-32">Component</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-28">Exception</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Message</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">No errors recorded for this window 🎉</td></tr>}
            {rows.map((r, i) => (
              <Fragment key={i}>
                <tr className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setExpanded(expanded === i ? null : i)} data-testid={`diag-error-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">{(r.created_at || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="px-3 py-2 font-mono text-[#002FA7] font-semibold">{r.component}</td>
                  <td className="px-3 py-2 font-mono text-red-600">{r.exc_type}</td>
                  <td className="px-3 py-2 text-slate-700 truncate max-w-[600px]" title={r.message}>{r.message}</td>
                </tr>
                {expanded === i && (
                  <tr className="border-b border-slate-100 bg-slate-50/40">
                    <td colSpan={4} className="px-3 py-3">
                      {Object.keys(r.context || {}).length > 0 && (
                        <div className="mb-2 text-[11px]">
                          <span className="font-bold text-slate-500 uppercase tracking-wider mr-2">Context:</span>
                          <code className="font-mono text-slate-700">{JSON.stringify(r.context, null, 0)}</code>
                        </div>
                      )}
                      {r.traceback && (
                        <pre className="bg-slate-900 text-slate-100 rounded px-3 py-2 text-[10px] font-mono overflow-x-auto leading-snug max-h-[300px]">{r.traceback}</pre>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SyncRunsTable({ runs }) {
  const rows = runs.rows || [];
  const byComp = runs.by_component || {};
  return (
    <div className="space-y-3">
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Component</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">OK</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Failed</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Avg duration</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(byComp).map(([k, v]) => (
              <tr key={k} className="border-b border-slate-100">
                <td className="px-3 py-2 font-mono font-semibold">{k}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-600">{v.ok || 0}</td>
                <td className={`px-3 py-2 text-right font-mono ${v.failed > 0 ? "text-red-600 font-bold" : "text-slate-400"}`}>{v.failed || 0}</td>
                <td className="px-3 py-2 text-right font-mono text-slate-500">{v.avg_duration_ms || 0}ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-32">Started</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-36">Component</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-20">Status</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-24">Duration</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Counts / Error</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No sync runs recorded in this window.</td></tr>}
            {rows.map((r) => {
              const stCls = r.status === "ok" ? "bg-emerald-50 text-emerald-700"
                : r.status === "failed" ? "bg-red-50 text-red-700"
                : "bg-amber-50 text-amber-700";
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`diag-run-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">{(r.started_at || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="px-3 py-2 font-mono text-[#002FA7] font-semibold">{r.component}</td>
                  <td className="px-3 py-2"><span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${stCls}`}>{r.status}</span></td>
                  <td className="px-3 py-2 text-right font-mono text-slate-500">{r.duration_ms ?? "—"}ms</td>
                  <td className="px-3 py-2">
                    {r.status === "failed" ? (
                      <span className="text-red-600 font-mono text-[11px]" title={r.error_message}>
                        <strong>{r.error_class}</strong>: {(r.error_message || "").slice(0, 100)}
                      </span>
                    ) : r.counts ? (
                      <span className="font-mono text-[11px] text-slate-600">{Object.entries(r.counts).map(([k, v]) => `${k}=${v}`).join(" · ")}</span>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PostbackHealthTable({ rows }) {
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50">
          <tr className="border-b border-slate-200">
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Publisher</th>
            <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total</th>
            <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Success</th>
            <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Failed</th>
            <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold w-24">Success %</th>
            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Top errors</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No outbound postbacks in this window.</td></tr>}
          {rows.map((r, i) => {
            const rate = r.success_rate_pct;
            const rateCls = rate >= 95 ? "text-emerald-600" : rate >= 70 ? "text-amber-600" : "text-red-600";
            return (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`diag-pb-row-${r.publisher_id || i}`}>
                <td className="px-3 py-2 text-slate-700">{r.publisher_email || <span className="font-mono text-slate-400">{r.publisher_id || "—"}</span>}</td>
                <td className="px-3 py-2 text-right font-mono">{r.total}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-600">{r.success}</td>
                <td className={`px-3 py-2 text-right font-mono ${r.fail > 0 ? "text-red-600 font-bold" : "text-slate-400"}`}>{r.fail}</td>
                <td className={`px-3 py-2 text-right font-mono font-bold ${rateCls}`}>{rate}%</td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                  {Object.entries(r.error_breakdown || {}).map(([k, v]) => (
                    <span key={k} className="inline-block mr-2 bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded">
                      {k} <span className="font-bold">{v}</span>
                    </span>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SummaryCard({ title, items }) {
  const entries = Object.entries(items);
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">{title}</div>
      {entries.length === 0 ? (
        <div className="text-xs text-slate-400">—</div>
      ) : (
        <div className="space-y-1">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-[11px] py-0.5">
              <span className="font-mono text-slate-700">{k}</span>
              <span className="font-mono font-bold text-slate-900 bg-slate-100 px-1.5 rounded">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
